import React from 'react';
import { useForm } from 'react-hook-form';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  VStack,
  Grid,
  GridItem,
  Textarea,
  Switch,
  FormHelperText,
  Box,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * فرم افزودن/ویرایش کاربر
 * @param {Object} props - props کامپوننت
 * @param {Object} props.user - اطلاعات کاربر برای ویرایش (اختیاری)
 * @param {Function} props.onSubmit - تابع ارسال فرم
 * @param {boolean} props.isLoading - وضعیت بارگذاری
 */
const UserForm = React.forwardRef(({ user, onSubmit, isLoading }, ref) => {
  const { isManager } = useAuth();
  const {
    register,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useForm({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      role: user?.role || 'admin',
      active: user?.active !== false,
    },
  });

  // قرار دادن getValues در ref
  React.useImperativeHandle(ref, () => ({
    getValues: () => getValues()
  }));

  const watchedRole = watch('role');

  return (
    <Box ref={ref}>
      <VStack spacing={4} align="stretch">
        {/* نام و نام خانوادگی */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.firstName}>
              <FormLabel>نام *</FormLabel>
              <Input
                {...register('firstName', {
                  required: 'نام الزامی است',
                  minLength: { value: 2, message: 'نام باید حداقل 2 کاراکتر باشد' },
                })}
                placeholder="نام کاربر"
              />
              <FormErrorMessage>
                {errors.firstName && errors.firstName.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.lastName}>
              <FormLabel>نام خانوادگی *</FormLabel>
              <Input
                {...register('lastName', {
                  required: 'نام خانوادگی الزامی است',
                  minLength: { value: 2, message: 'نام خانوادگی باید حداقل 2 کاراکتر باشد' },
                })}
                placeholder="نام خانوادگی کاربر"
              />
              <FormErrorMessage>
                {errors.lastName && errors.lastName.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        {/* ایمیل و شماره تماس */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <GridItem>
            <FormControl isInvalid={errors.email}>
              <FormLabel>ایمیل</FormLabel>
              <Input
                type="email"
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'ایمیل معتبر وارد کنید',
                  },
                  validate: (value) => {
                    if (value && value.trim() !== '') {
                      return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value) || 'ایمیل معتبر وارد کنید';
                    }
                    return true; // اجازه خالی بودن
                  }
                })}
                placeholder="example@email.com (اختیاری)"
              />
              <FormErrorMessage>
                {errors.email && errors.email.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isInvalid={errors.phone}>
              <FormLabel>شماره تماس</FormLabel>
              <Input
                {...register('phone', {
                  pattern: {
                    value: /^09\d{9}$/,
                    message: 'شماره تماس معتبر وارد کنید (مثال: 09123456789)',
                  },
                })}
                placeholder="09123456789"
              />
              <FormErrorMessage>
                {errors.phone && errors.phone.message}
              </FormErrorMessage>
            </FormControl>
          </GridItem>
        </Grid>

        {/* نقش */}
        <FormControl isInvalid={errors.role}>
          <FormLabel>نقش *</FormLabel>
          <Select
            {...register('role', {
              required: 'انتخاب نقش الزامی است',
            })}
            placeholder="نقش کاربر را انتخاب کنید"
          >
            <option value="admin">مدیر</option>
            {isManager() && <option value="manager">مدیر کل</option>}
          </Select>
          <FormErrorMessage>
            {errors.role && errors.role.message}
          </FormErrorMessage>
        </FormControl>



        {/* وضعیت فعال بودن */}
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="active" mb="0">
            کاربر فعال
          </FormLabel>
          <Switch
            id="active"
            {...register('active')}
            colorScheme="teal"
          />
          <FormHelperText mr={3}>
            کاربر فعال می‌تواند وارد سیستم شود
          </FormHelperText>
        </FormControl>
      </VStack>
    </Box>
  );
});

export default UserForm; 